import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';

/**
 * `POST /fiscal/calculate` — simulacao pelo motor fiscal real.
 *
 * A rota nao grava nada: monta o `TaxCalculationContext`, avalia as regras do
 * tenant (`TaxRuleEngine`), busca as aliquotas vigentes na data pedida
 * (`TaxRateRepository`) e devolve o calculo com a trilha de auditoria. Por
 * ser pura, da para consultar como query — e por isso o comparativo A/B e
 * so a mesma chamada com dois conjuntos de parametros.
 */

/** Espelha `OperationType` (domain/tax-engine/value-objects/tax-calculation-context.ts). */
export type OperationType =
  | 'VENDA'
  | 'COMPRA'
  | 'DEVOLUCAO_VENDA'
  | 'DEVOLUCAO_COMPRA'
  | 'TRANSFERENCIA'
  | 'SERVICO'
  | 'OUTROS';

/** Espelha `TaxRegimeCode` (domain/tax-engine/value-objects/tax-regime.ts). */
export type TaxRegimeCode =
  | 'SIMPLES_NACIONAL'
  | 'SIMPLES_NACIONAL_EXCESSO'
  | 'SIMPLES_MEI'
  | 'LUCRO_PRESUMIDO'
  | 'LUCRO_REAL';

export const OPERATION_TYPE_LABEL: Record<OperationType, string> = {
  VENDA: 'Venda',
  COMPRA: 'Compra',
  DEVOLUCAO_VENDA: 'Devolução de venda',
  DEVOLUCAO_COMPRA: 'Devolução de compra',
  TRANSFERENCIA: 'Transferência',
  SERVICO: 'Serviço',
  OUTROS: 'Outros',
};

export const REGIME_LABEL: Record<TaxRegimeCode, string> = {
  SIMPLES_NACIONAL: 'Simples Nacional',
  SIMPLES_NACIONAL_EXCESSO: 'Simples Nacional — excesso de sublimite',
  SIMPLES_MEI: 'MEI',
  LUCRO_PRESUMIDO: 'Lucro Presumido',
  LUCRO_REAL: 'Lucro Real',
};

/** Espelha `CalculateItemDto`. */
export interface CalculateItemInput {
  productId?: string;
  ncmCode: string;
  cfopCode?: string;
  quantity: number;
  unitValue: number;
  serviceId?: string;
}

/** Espelha `CalculateTaxesRequestDto`. */
export interface CalculateTaxesInput {
  operationType: OperationType;
  originUf: string;
  destinationUf: string;
  emitterRegime: TaxRegimeCode;
  destinationRegime?: TaxRegimeCode;
  items: CalculateItemInput[];
  /** `YYYY-MM-DD`. Escolhe as aliquotas por vigencia e a fase da reforma (IBS/CBS a partir de 2026). */
  date?: string;
}

/** Espelha `CalculatedTaxLineDto`. */
export interface CalculatedTaxLine {
  base: number;
  value: number;
  aliquota: number;
  cst: string;
}

/** Espelha `CalculatedItemDetailDto`. */
export interface CalculatedItemDetail {
  index: number;
  productId: string;
  ncm: string;
  cfop: string;
  quantity: number;
  unitValue: number;
  totalValue: number;
  taxes: Record<string, CalculatedTaxLine>;
}

export interface CalculatedTaxTotal {
  base: number;
  value: number;
}

/** Espelha `CalculatedTotalsDto`. */
export interface CalculatedTotals {
  icms: CalculatedTaxTotal;
  icmsSt: CalculatedTaxTotal;
  pis: CalculatedTaxTotal;
  cofins: CalculatedTaxTotal;
  ipi: CalculatedTaxTotal;
  iss: CalculatedTaxTotal;
  ibs: CalculatedTaxTotal;
  cbs: CalculatedTaxTotal;
  fcp: CalculatedTaxTotal;
  totalTax: number;
}

export interface CalculatedAuditEntry {
  step: string;
  timestamp: string;
  details: Record<string, unknown>;
}

/** Espelha `CalculateTaxesResponseDto`. */
export interface CalculateTaxesResponse {
  success: boolean;
  itemDetails: CalculatedItemDetail[];
  totals: CalculatedTotals;
  cfopResolved: { code: string; description: string };
  ruleApplied: { id: string; name: string } | null;
  auditTrail: {
    entries: CalculatedAuditEntry[];
    totalRulesEvaluated: number;
    ruleApplied: { id: string; name: string } | null;
    calculatedAt: string;
  };
  errors: string[];
}

/** As chaves de `CalculatedTotals` que sao tributos, na ordem em que a nota os apresenta. */
export const TAX_KEYS = [
  'icms',
  'icmsSt',
  'ipi',
  'pis',
  'cofins',
  'iss',
  'fcp',
  'ibs',
  'cbs',
] as const;

export type TaxKey = (typeof TAX_KEYS)[number];

export const TAX_LABEL: Record<TaxKey, string> = {
  icms: 'ICMS',
  icmsSt: 'ICMS-ST',
  ipi: 'IPI',
  pis: 'PIS',
  cofins: 'COFINS',
  iss: 'ISS',
  fcp: 'FCP',
  ibs: 'IBS',
  cbs: 'CBS',
};

/** Nome do tributo em `itemDetails[].taxes`, que usa o codigo do motor. */
export const TAX_DETAIL_KEY: Record<TaxKey, string> = {
  icms: 'ICMS',
  icmsSt: 'ICMS_ST',
  ipi: 'IPI',
  pis: 'PIS',
  cofins: 'COFINS',
  iss: 'ISS',
  fcp: 'FCP',
  ibs: 'IBS',
  cbs: 'CBS',
};

/**
 * Um cenario do comparativo.
 *
 * `enabled` fica falso enquanto os parametros nao formam um pedido valido —
 * sem item, sem NCM ou com valor zero o motor nao tem o que calcular.
 */
export function useTaxCalculation(input: CalculateTaxesInput | null) {
  return useQuery<CalculateTaxesResponse>({
    queryKey: ['fiscal', 'calculate', input],
    queryFn: () => apiClient.post<CalculateTaxesResponse>('/fiscal/calculate', input),
    enabled: input !== null,
    // Simulacao e deterministica para os mesmos parametros: nao ha por que
    // refazer a chamada ao voltar para a aba.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** Soma das bases dos itens — o denominador da aliquota efetiva. */
export function baseTotal(result: CalculateTaxesResponse | undefined): number {
  if (!result) return 0;
  return result.itemDetails.reduce((soma, item) => soma + item.totalValue, 0);
}

/**
 * Aliquota efetiva: tributo total sobre o valor dos itens.
 *
 * Derivada, nao inventada — e a divisao de dois numeros que o motor devolveu.
 * Devolve `null` quando nao ha base, para a tela nao imprimir 0% nem NaN.
 */
export function effectiveRate(result: CalculateTaxesResponse | undefined): number | null {
  const base = baseTotal(result);
  if (!result || base <= 0) return null;
  return (result.totals.totalTax / base) * 100;
}

/** Valor de um tributo em um item, 0 quando o motor nao o aplicou. */
export function itemTaxValue(item: CalculatedItemDetail | undefined): number {
  if (!item) return 0;
  return Object.values(item.taxes).reduce((soma, linha) => soma + linha.value, 0);
}
